import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Avatar, Chip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import BaseWrapper from "../../components/ui/BaseWrapper";
import BaseDataTableV2 from "../../components/ui/BaseDataTableV2";
import BaseTextField from "../../components/ui/BaseTextField";
import TableColumnSettings from "../../components/ui/TableColumnSettings";
import useTableColumnSettings from "../../hooks/useTableColumnSettings";
import useAuth from "../../hooks/useAuth";
import { getFreelanceCompanyMembers } from "../../services/api/freelanceCompany";
import BaseTooltip from "../../components/ui/BaseTooltip";
import { debounce, formatPhoneNumber } from "../../utils/helpers";
import { joinNonEmpty } from "../../utils/displayValue";
import dayjs from "dayjs";

const LOCKED_COLUMN_FIELDS = ["userId"];

const WorkersTable = () => {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const [pagination, setPagination] = useState({ page: 0, pageSize: 10 });
  const [search, setSearch] = useState("");
  const [sortModel, setSortModel] = useState([]);

  const debouncedSearchRef = useRef(
    debounce((value) => {
      setSearch(value);
      setPagination((p) => ({ ...p, page: 0 }));
    }, 500),
  );

  const handleSortModelChange = (newModel) => {
    setSortModel(newModel);
    setPagination((p) => ({ ...p, page: 0 }));
  };

  const columns = [
    {
      field: "userId",
      headerName: "ID",
      flex: 0.5,
      sortable: true,
      renderCell: (params) => (
        <p
          className="table-font-size font-normal text-primary ml-2 cursor-pointer"
          onClick={() => navigate(`/user/details/profile/${params.row.user.id}`)}
        >
          {params.row.user.id}
        </p>
      ),
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1.5,
      sortable: true,
      renderCell: (params) => (
        <div
          className="flex items-center gap-3 cursor-pointer"
          onClick={() => navigate(`/user/details/profile/${params.row.user.id}`)}
        >
          <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>
            {params.row.user.firstName?.[0]}
            {params.row.user.lastName?.[0]}
          </Avatar>
          <p className="table-font-size font-normal capitalize text-primary">
            {params.row.user.firstName} {params.row.user.lastName}
          </p>
        </div>
      ),
    },
    {
      field: "address",
      headerName: "Address",
      flex: 1.5,
      renderCell: (params) => {
        const address = params.row.user.personalAddress;
        const fullAddress = joinNonEmpty([address?.addressLine1, address?.country]);

        if (!fullAddress) return <p className="table-font-size cursor-default"></p>;

        return (
          <BaseTooltip placement="top" content={<p className="text-white">{fullAddress}</p>}>
            <p className="table-font-size cursor-default truncate overflow-hidden whitespace-nowrap">
              {`${address?.addressLine1 || ""}`}
            </p>
          </BaseTooltip>
        );
      },
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.5,
      sortable: false,
      renderCell: (params) => <p className="table-font-size">{params.row.user.email}</p>,
    },
    {
      field: "phoneNumber",
      headerName: "Mobile",
      flex: 1.5,
      sortable: false,
      renderCell: (params) => (
        <p className="table-font-size">{formatPhoneNumber(params.row.user.phoneNumber) || "—"}</p>
      ),
    },
    {
      field: "createdAt",
      headerName: "Date",
      flex: 1.5,
      sortable: true,
      renderCell: (params) => (
        <p className="table-font-size">{dayjs(params.row.createdAt).format("DD-MM-YYYY")}</p>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      sortable: true,
      renderCell: (params) =>
        params.row.user.status === "APPROVED" || params.row.user.status === "ACTIVATED" ? (
          <Chip label="Active" color="primary" size="small" className="w-24 justify-center" />
        ) : params.row.user.status === "DEACTIVATED" ? (
          <Chip label="In-Active" color="error" size="small" className="w-24 justify-center" />
        ) : (
          <Chip
            label={params.row.user.status === "SUBMITTED" ? "Submitted" : "Pending"}
            color="primary"
            size="small"
            variant="outlined"
            className="w-24 justify-center"
          />
        ),
    },
  ];

  const columnSettings = useTableColumnSettings({
    columns,
    storageKey: `freelanceCompanyWorkersColumnSettings:${auth?.user?.id || "guest"}`,
    lockedFields: LOCKED_COLUMN_FIELDS,
  });

  // Only the visible columns' data is fetched.
  const visibleColumns = columnSettings.visibleFields.join(",");

  const queryParams = {
    page: pagination.page + 1,
    limit: pagination.pageSize,
    search,
    columns: visibleColumns,
    ...(sortModel.length > 0 ? { sortBy: sortModel[0].field, sortOrder: sortModel[0].sort } : {}),
  };

  const { isPending, data } = useQuery({
    queryKey: ["freelanceCompanyMembers", queryParams],
    queryFn: () => getFreelanceCompanyMembers(queryParams),
    placeholderData: (prev) => prev,
  });

  const members = data?.data?.data || [];
  const totalCount = data?.data?.totalCount || 0;

  return (
    <BaseWrapper>
      <div className="bg-white rounded-lg px-5 py-2 shadow-md mt-2">
        <div className="flex justify-end items-center gap-2 w-full">
          <button
            type="button"
            onClick={columnSettings.open}
            className="flex items-center gap-2 h-[36px] px-3 rounded-md border border-primary text-primary text-[13px] font-light hover:bg-[#EAF1FF] whitespace-nowrap"
            title="Customize columns"
          >
            <i className="fa-light fa-table-columns text-sm" />
            <span>Customize View</span>
          </button>
          <div className="w-[28%]">
            <BaseTextField
              placeholder="Search by name, email or ID"
              onChange={(e) => debouncedSearchRef.current(e.target.value)}
              startAdornment={<i className="fa-solid fa-magnifying-glass text-lg"></i>}
            />
          </div>
        </div>
      </div>

      <div className="mt-2">
        <BaseDataTableV2
          className="max-h-[calc(100vh-300px)]"
          rowHeight={38}
          columns={columnSettings.orderedColumns}
          columnVisibilityModel={columnSettings.columnVisibilityModel}
          data={members}
          isLoading={isPending}
          disableColumnResize={false}
          disableRowSelectionOnClick
          hideFooter={false}
          pagination
          rowCount={totalCount}
          pageSizeOptions={[10, 20, 50]}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          paginationMode="server"
          disableColumnSorting={false}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          sortingOrder={["asc", "desc"]}
        />
      </div>

      {columnSettings.isOpen && (
        <TableColumnSettings
          open={columnSettings.isOpen}
          onClose={columnSettings.close}
          title="Customize Columns"
          columns={columnSettings.columnSettingsList}
          visibility={columnSettings.columnVisibility}
          lockedFields={LOCKED_COLUMN_FIELDS}
          onApply={columnSettings.applySettings}
          onReset={columnSettings.resetSettings}
        />
      )}
    </BaseWrapper>
  );
};

export default WorkersTable;
